import React from 'react';
import { useTranslation } from 'react-i18next';
import SeoMeta from '../components/SeoMeta';
import { cookiesTranslations } from '../i18n/cookies-translations';

const COOKIES = [
  'session', 'csrf', 'consent', 'lang', 'ga', 'gid', 'gat',
];

const BROWSER_LINKS = {
  chrome: 'https://support.google.com/chrome/answer/95647?hl=es',
  firefox: 'https://support.mozilla.org/es/kb/cookies-informacion-que-los-sitios-web-guardan-en-',
  edge: 'https://support.microsoft.com/es-es/microsoft-edge/eliminar-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09',
  safari: 'https://support.apple.com/es-es/guide/safari/sfri11471/mac',
};

const CookiesPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'es';
  const t = (key) => cookiesTranslations[lang][key] || cookiesTranslations['es'][key] || key;

  return (
    <>
      <SeoMeta
        title={t('page-title')}
        description={t('page-description')}
      />

      <div className="bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">
              {t('title')}
            </h1>

            <p className="text-sm text-gray-600 mb-8">
              {t('last-updated')}
            </p>

            {/* 1. Introducción */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('section-1-title')}
              </h2>
              <p className="text-gray-700 mb-4">{t('section-1-p1')}</p>
              <p className="text-gray-700">{t('section-1-p2')}</p>
            </section>

            {/* 2. Responsable */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('section-2-title')}
              </h2>
              <p className="text-gray-700 mb-4">{t('section-2-p1')}</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>{t('section-2-company')}</strong></li>
                <li>{t('section-2-cif')}</li>
                <li>{t('section-2-address')}</li>
                <li>{t('section-2-email')}</li>
              </ul>
            </section>

            {/* 3. Qué son las cookies */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('section-3-title')}
              </h2>
              <p className="text-gray-700 mb-4">{t('section-3-p1')}</p>
              <p className="text-gray-700">{t('section-3-p2')}</p>
            </section>

            {/* 4. Tipos de cookies */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('section-4-title')}
              </h2>
              <p className="text-gray-700 mb-4">{t('section-4-p1')}</p>
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="font-semibold text-gray-900">{t('section-4-technical-title')}</h3>
                  <p className="text-gray-700">{t('section-4-technical-desc')}</p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h3 className="font-semibold text-gray-900">{t('section-4-analytics-title')}</h3>
                  <p className="text-gray-700">{t('section-4-analytics-desc')}</p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="font-semibold text-gray-900">{t('section-4-marketing-title')}</h3>
                  <p className="text-gray-700">{t('section-4-marketing-desc')}</p>
                </div>
              </div>
            </section>

            {/* 5. Tabla de cookies */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('section-5-title')}
              </h2>
              <p className="text-gray-700 mb-4">{t('section-5-p1')}</p>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold">{t('table-name')}</th>
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold">{t('table-type')}</th>
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold">{t('table-purpose')}</th>
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold">{t('table-duration')}</th>
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold">{t('table-owner')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COOKIES.map((cookie) => (
                      <tr key={cookie}>
                        <td className="border border-gray-300 px-4 py-2 font-mono text-sm">{t(`cookie-${cookie}-name`)}</td>
                        <td className="border border-gray-300 px-4 py-2">{t(`cookie-${cookie}-type`)}</td>
                        <td className="border border-gray-300 px-4 py-2">{t(`cookie-${cookie}-purpose`)}</td>
                        <td className="border border-gray-300 px-4 py-2">{t(`cookie-${cookie}-duration`)}</td>
                        <td className="border border-gray-300 px-4 py-2">{t(`cookie-${cookie}-owner`)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* 6. Cómo desactivar cookies */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('section-6-title')}
              </h2>
              <p className="text-gray-700 mb-4">{t('section-6-p1')}</p>
              <p className="text-gray-700 mb-4">{t('section-6-p2')}</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                {Object.entries(BROWSER_LINKS).map(([browser, url]) => (
                  <li key={browser}>
                    <strong>{t(`section-6-${browser}`)}:</strong>{' '}
                    <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {url}
                    </a>
                  </li>
                ))}
              </ul>
              <p className="text-gray-700">{t('section-6-p3')}</p>
            </section>

            {/* 7. Terceros */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('section-7-title')}
              </h2>
              <p className="text-gray-700 mb-4">{t('section-7-p1')}</p>
              <p className="text-gray-700">
                {t('section-7-p2')}{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  https://policies.google.com/privacy
                </a>
              </p>
            </section>

            {/* 8. Conservación */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('section-8-title')}
              </h2>
              <p className="text-gray-700">{t('section-8-p1')}</p>
            </section>

            {/* 9. Actualizaciones */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('section-9-title')}
              </h2>
              <p className="text-gray-700">{t('section-9-p1')}</p>
            </section>

            {/* Footer */}
            <div className="border-t border-gray-200 pt-6 mt-8">
              <p className="text-sm text-gray-600 mb-2">{t('footer-copyright')}</p>
              <p className="text-sm text-gray-600">
                {t('footer-contact')}{' '}
                <a href={`mailto:${t('footer-email')}`} className="text-blue-600 hover:underline">
                  {t('footer-email')}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookiesPage;
